import { CourseLessonEditor } from '@/features/course/CourseLessonEditor';
import { CourseDocuments } from '@/features/course/CourseDocuments';
import { CourseTeams } from '@/features/course/CourseTeams';
import { useEffect, useState, type FC } from 'react';
import { studentApi } from '@/features/student/api';
import { teacherApi } from '../api';
import type {
  TeacherAssignment,
  TeacherDiscipline,
  TeacherGroup,
  TeacherLessonWeek,
  TeacherProfile,
  TeacherStudent,
  TeacherSubmission,
} from '../types';
import {
  AcademicCapIcon,
  ArrowLeftIcon,
  BookOpenIcon,
  CalendarIcon,
  ChartBarIcon,
  CheckSquareIcon,
  CloseIcon,
  DocumentTextIcon,
  FolderIcon,
  PlusIcon,
  UsersIcon,
} from './TeacherIcons';

interface TeacherCourseWorkspaceProps {
  disciplines: TeacherDiscipline[];
  selectedDisciplineCode: string;
  onSelectDiscipline: (code: string) => void;
  groups: TeacherGroup[];
  selectedGroupCode: string;
  onSelectGroup: (code: string) => void;
  students: TeacherStudent[];
  weeks: TeacherLessonWeek[];
  assignments: TeacherAssignment[];
  submissions: TeacherSubmission[];
  profile?: TeacherProfile | null;
  onBackToCabinet: () => void;
  onSaveWorkspace: () => void;
  onCreateDiscipline: (payload: { code: string; title: string; credits: number; degree_level: string }) => Promise<void>;
  onCreateAssignment: (payload: Partial<TeacherAssignment>) => Promise<void>;
  onGradeSubmission: (submissionId: string, score: number | null, feedback: string, status?: string) => Promise<void>;
  onUpdateWeekContent: (payload: Partial<TeacherLessonWeek> & { discipline_code: string; week_number: number }) => Promise<void>;
}

type WorkspaceNav =
  | 'disciplines-groups'
  | 'lessons'
  | 'assignments'
  | 'submissions'
  | 'team-projects'
  | 'materials'
  | 'analytics';

export const TeacherCourseWorkspace: FC<TeacherCourseWorkspaceProps> = ({
  disciplines,
  selectedDisciplineCode,
  onSelectDiscipline,
  groups,
  selectedGroupCode,
  onSelectGroup,
  students,
  weeks,
  assignments,
  submissions,
  profile,
  onBackToCabinet,
  onSaveWorkspace,
  onCreateDiscipline,
  onCreateAssignment,
  onGradeSubmission,
  onUpdateWeekContent,
}) => {
  const [activeNav, setActiveNav] = useState<WorkspaceNav>('disciplines-groups');
  const [selectedWeekNum, setSelectedWeekNum] = useState<number>(1);
  const [isAddDisciplineOpen, setIsAddDisciplineOpen] = useState(false);
  const [isAddAssignmentOpen, setIsAddAssignmentOpen] = useState(false);
  const [selectedStudentForModal, setSelectedStudentForModal] = useState<TeacherStudent | null>(null);

  const [newDiscCode, setNewDiscCode] = useState('');
  const [newDiscTitle, setNewDiscTitle] = useState('');
  const [newDiscCredits, setNewDiscCredits] = useState(5);
  const [newDiscLevel, setNewDiscLevel] = useState('Бакалавриат');

  const [editingAssignmentId, setEditingAssignmentId] = useState<string | undefined>();
  const [assignmentStatus, setAssignmentStatus] = useState('Опубликовано');
  const [actionError, setActionError] = useState('');
  const [enrollUserId, setEnrollUserId] = useState('');
  const [enrollGroup, setEnrollGroup] = useState('');
  const [calendarWeeks, setCalendarWeeks] = useState(15);
  const [rk1Week, setRk1Week] = useState(7);
  const [rk2Week, setRk2Week] = useState(15);
  const [weightedGrading, setWeightedGrading] = useState(false);
  const [weights, setWeights] = useState<Record<string, number>>({ PRACTICE: 30, SRO: 20, CONTROL: 20, FINAL: 30 });
  const [newAssignTitle, setNewAssignTitle] = useState('');
  const [newAssignType, setNewAssignType] = useState('ПРАКТИКА');
  const [newAssignWeek, setNewAssignWeek] = useState(selectedWeekNum);
  const [newAssignDeadline, setNewAssignDeadline] = useState('');
  const [newAssignMaxPoints, setNewAssignMaxPoints] = useState(10);
  const [newAssignDesc, setNewAssignDesc] = useState('');

  const [gradingSubId, setGradingSubId] = useState<string | null>(null);
  const [gradingScore, setGradingScore] = useState<number>(10);
  const [gradingFeedback, setGradingFeedback] = useState<string>('');

  const currentDiscipline = disciplines.find((d) => d.code === selectedDisciplineCode) || disciplines[0];
  const selectedWeek = weeks.find((w) => w.week_number === selectedWeekNum) || { week_number: selectedWeekNum, publication_status: 'Черновик' } as Partial<TeacherLessonWeek>;

  useEffect(() => {
    setSelectedStudentForModal(null); setGradingSubId(null); setIsAddAssignmentOpen(false); setEditingAssignmentId(undefined);
    setSelectedWeekNum(1); setActionError('');
    const calendar = disciplines.find(d => d.code === selectedDisciplineCode)?.calendar_json;
    setCalendarWeeks(calendar?.weeks || 15); setRk1Week(calendar?.rk1_week || 7); setRk2Week(calendar?.rk2_week || 15);
    setWeightedGrading(Boolean(calendar?.grading_weights));
    if (calendar?.grading_weights) setWeights(calendar.grading_weights);
  }, [selectedDisciplineCode, selectedGroupCode]);
  const perform = async (action: () => Promise<unknown>) => {
    try { setActionError(''); await action(); } catch (e) { setActionError(e instanceof Error ? e.message : 'Не удалось сохранить'); }
  };
  const uploadMaterial = async (file: File, field: 'lecture_file_url' | 'practice_file_url') => {
    const uploaded = await studentApi.uploadCourseFile(selectedDisciplineCode, file);
    await onUpdateWeekContent({ discipline_code: selectedDisciplineCode, week_number: selectedWeekNum, [field]: uploaded.url,
      publication_status: 'Черновик' });
  };
  const handleCreateDisciplineSubmit = async () => {
    if (!newDiscCode.trim() || !newDiscTitle.trim()) return;
    await onCreateDiscipline({
      code: newDiscCode.trim(),
      title: newDiscTitle.trim(),
      credits: Number(newDiscCredits),
      degree_level: newDiscLevel,
    });
    setNewDiscCode('');
    setNewDiscTitle('');
    setIsAddDisciplineOpen(false);
  };

  const handleCreateAssignmentSubmit = async () => {
    if (!newAssignTitle.trim()) return;
    await onCreateAssignment({
      id: editingAssignmentId,
      discipline_code: selectedDisciplineCode,
      group_code: selectedGroupCode,
      assignment_type: newAssignType,
      week_number: Number(newAssignWeek),
      title: newAssignTitle.trim(),
      description: newAssignDesc.trim() || undefined,
      deadline: newAssignDeadline,
      max_points: Number(newAssignMaxPoints),
      status: assignmentStatus,
      rubric_json: {
        'Полнота анализа': Math.round(Number(newAssignMaxPoints) * 0.4),
        'Аргументация': Math.round(Number(newAssignMaxPoints) * 0.3),
        'Оформление': Math.round(Number(newAssignMaxPoints) * 0.2),
        'Источники': Math.round(Number(newAssignMaxPoints) * 0.1),
      },
    });
    setEditingAssignmentId(undefined);
    setNewAssignTitle('');
    setNewAssignDesc('');
    setIsAddAssignmentOpen(false);
  };

  const handleGradeSubmit = async () => {
    if (!gradingSubId) return;
    await onGradeSubmission(gradingSubId, gradingScore, gradingFeedback);
    setGradingSubId(null);
    setGradingFeedback('');
  };

  return (
    <div className="space-y-6">
      {actionError && <p role="alert" className="text-red-400">{actionError}</p>}
      {selectedDisciplineCode && <details className="rounded-xl border border-slate-700 p-3 text-sm">
        <summary>Назначения и календарь дисциплины</summary>
        <input aria-label="Academia ID обучающегося" placeholder="Academia ID обучающегося" value={enrollUserId} onChange={e => setEnrollUserId(e.target.value)} />
        <input aria-label="Учебная группа" placeholder="Учебная группа" value={enrollGroup} onChange={e => setEnrollGroup(e.target.value)} />
        <button disabled={!enrollUserId || !enrollGroup} onClick={() => void perform(async () => { await teacherApi.enroll(selectedDisciplineCode, enrollGroup, enrollUserId); onSaveWorkspace(); })}>Назначить обучающегося</button>
        <label>Учебных недель <input type="number" value={calendarWeeks} onChange={e => setCalendarWeeks(Number(e.target.value))} /></label>
        <label>Неделя РК1 <input type="number" value={rk1Week} onChange={e => setRk1Week(Number(e.target.value))} /></label>
        <label>Неделя РК2 <input type="number" value={rk2Week} onChange={e => setRk2Week(Number(e.target.value))} /></label>
        <label><input type="checkbox" checked={weightedGrading} onChange={e => setWeightedGrading(e.target.checked)} />Взвешенная формула оценивания</label>
        {weightedGrading && Object.entries({ PRACTICE: 'Практика/текущая работа', SRO: 'СРО', CONTROL: 'РК', FINAL: 'Итог' }).map(([key,label]) => <label key={key}>{label} (%) <input type="number" min={0} max={100} value={weights[key]} onChange={e => setWeights(prev => ({ ...prev, [key]: Number(e.target.value) }))} /></label>)}
        <button onClick={() => void perform(() => teacherApi.configureCalendar(selectedDisciplineCode, calendarWeeks, rk1Week, rk2Week, weightedGrading ? weights : null))}>Сохранить календарь</button>
      </details>}
      {activeNav === 'lessons' && <CourseLessonEditor key={`${selectedDisciplineCode}-${selectedWeekNum}-${selectedWeek.lecture_title}`} code={selectedDisciplineCode} week={selectedWeek} onSave={onUpdateWeekContent} />}
      {activeNav === 'lessons' && <button onClick={() => void perform(() => onUpdateWeekContent({ discipline_code: selectedDisciplineCode, week_number: selectedWeekNum, publication_status: selectedWeek.publication_status === 'Опубликовано' ? 'Черновик' : 'Опубликовано' }))}>{selectedWeek.publication_status === 'Опубликовано' ? 'Снять материалы с публикации' : 'Опубликовать материалы недели'}</button>}
      <div className="flex flex-col gap-4 rounded-2xl border border-slate-800 bg-[#09101d] p-5 shadow-xl sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBackToCabinet}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-700 bg-slate-800/80 text-slate-300 hover:border-cyan-500/50 hover:bg-slate-700 hover:text-white transition-all"
            title="Вернуться в кабинет"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                Кабинет преподавателя
              </span>
            </div>
            <h2 className="text-xl font-bold text-white">Преподаватель</h2>
            <p className="text-xs text-slate-400">
              Дисциплины · занятия · СРО / СРОП · задания · журнал · группы · проекты · аналитика.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsAddDisciplineOpen(true)}
            className="flex items-center gap-2 rounded-xl border border-cyan-500/30 bg-cyan-950/40 px-4 py-2 text-xs font-semibold text-cyan-300 hover:border-cyan-400 hover:bg-cyan-900/50 transition-all"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Дисциплина</span>
          </button>
          <button
            onClick={onSaveWorkspace}
            className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-2 text-xs font-bold text-slate-950 hover:from-amber-300 hover:to-amber-400 shadow-md hover:shadow-amber-500/20 transition-all"
          >
            <span>Сохранить</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="space-y-4 lg:col-span-1">
          <div className="rounded-2xl border border-cyan-500/30 bg-[#0c1626] p-5 shadow-lg">
            <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">Учебное пространство</p>
            <h3 className="mt-1 text-base font-bold text-white">{profile?.full_name || '—'}</h3>
            <p className="mt-1 text-xs text-slate-400 leading-relaxed">
              {profile?.department || 'Кафедра казахского языкознания'}
              <br />
              {profile?.university || 'Евразийский национальный университет имени Л.Н. Гумилева'}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-[#09101d] p-2 shadow-lg space-y-1">
            <button
              onClick={() => setActiveNav('disciplines-groups')}
              className={`flex w-full items-center justify-between rounded-xl px-3.5 py-3 text-left text-xs font-semibold transition-all ${
                activeNav === 'disciplines-groups'
                  ? 'border border-cyan-500/40 bg-cyan-950/50 text-cyan-300 shadow-inner'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <BookOpenIcon className="h-4 w-4" />
                <div>
                  <p className="font-bold">Дисциплины и группы</p>
                  <p className="text-[10px] text-slate-400 font-normal">Управление учебным процессом</p>
                </div>
              </div>
              <span className="text-slate-500">›</span>
            </button>

            <button
              onClick={() => setActiveNav('lessons')}
              className={`flex w-full items-center justify-between rounded-xl px-3.5 py-3 text-left text-xs font-semibold transition-all ${
                activeNav === 'lessons'
                  ? 'border border-cyan-500/40 bg-cyan-950/50 text-cyan-300 shadow-inner'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <CalendarIcon className="h-4 w-4" />
                <div>
                  <p className="font-bold">Учебные занятия</p>
                  <p className="text-[10px] text-slate-400 font-normal">Управление учебным процессом</p>
                </div>
              </div>
              <span className="text-slate-500">›</span>
            </button>

            <button
              onClick={() => setActiveNav('assignments')}
              className={`flex w-full items-center justify-between rounded-xl px-3.5 py-3 text-left text-xs font-semibold transition-all ${
                activeNav === 'assignments'
                  ? 'border border-cyan-500/40 bg-cyan-950/50 text-cyan-300 shadow-inner'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <CheckSquareIcon className="h-4 w-4" />
                <div>
                  <p className="font-bold">Задания</p>
                  <p className="text-[10px] text-slate-400 font-normal">Управление учебным процессом</p>
                </div>
              </div>
              <span className="text-slate-500">›</span>
            </button>

            <button
              onClick={() => setActiveNav('submissions')}
              className={`flex w-full items-center justify-between rounded-xl px-3.5 py-3 text-left text-xs font-semibold transition-all ${
                activeNav === 'submissions'
                  ? 'border border-cyan-500/40 bg-cyan-950/50 text-cyan-300 shadow-inner'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="h-4 w-4" />
                <div>
                  <p className="font-bold">Проверка работ</p>
                  <p className="text-[10px] text-slate-400 font-normal">Управление учебным процессом</p>
                </div>
              </div>
              <span className="text-slate-500">›</span>
            </button>

            <button
              onClick={() => setActiveNav('team-projects')}
              className={`flex w-full items-center justify-between rounded-xl px-3.5 py-3 text-left text-xs font-semibold transition-all ${
                activeNav === 'team-projects'
                  ? 'border border-cyan-500/40 bg-cyan-950/50 text-cyan-300 shadow-inner'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <UsersIcon className="h-4 w-4" />
                <div>
                  <p className="font-bold">Командные проекты</p>
                  <p className="text-[10px] text-slate-400 font-normal">Управление учебным процессом</p>
                </div>
              </div>
              <span className="text-slate-500">›</span>
            </button>

            <button
              onClick={() => setActiveNav('materials')}
              className={`flex w-full items-center justify-between rounded-xl px-3.5 py-3 text-left text-xs font-semibold transition-all ${
                activeNav === 'materials'
                  ? 'border border-cyan-500/40 bg-cyan-950/50 text-cyan-300 shadow-inner'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <FolderIcon className="h-4 w-4" />
                <div>
                  <p className="font-bold">Материалы дисциплины</p>
                  <p className="text-[10px] text-slate-400 font-normal">Управление учебным процессом</p>
                </div>
              </div>
              <span className="text-slate-500">›</span>
            </button>

            <button
              onClick={() => setActiveNav('analytics')}
              className={`flex w-full items-center justify-between rounded-xl px-3.5 py-3 text-left text-xs font-semibold transition-all ${
                activeNav === 'analytics'
                  ? 'border border-cyan-500/40 bg-cyan-950/50 text-cyan-300 shadow-inner'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <ChartBarIcon className="h-4 w-4" />
                <div>
                  <p className="font-bold">Аналитика дисциплины</p>
                  <p className="text-[10px] text-slate-400 font-normal">Управление учебным процессом</p>
                </div>
              </div>
              <span className="text-slate-500">›</span>
            </button>
          </div>
        </div>

        <div className="space-y-6 lg:col-span-3">
          <div className="rounded-2xl border border-cyan-500/30 bg-[#09101d] p-5 shadow-xl">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                  Активная дисциплина · 2026–2027 · Осенний семестр
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2.5">
              {disciplines.map((d) => (
                <button
                  key={d.code}
                  onClick={() => onSelectDiscipline(d.code)}
                  className={`rounded-xl px-4 py-2.5 text-xs font-bold transition-all ${
                    selectedDisciplineCode === d.code
                      ? 'border-2 border-cyan-400 bg-cyan-950/80 text-white shadow-lg shadow-cyan-500/20'
                      : 'border border-slate-700 bg-slate-900/80 text-slate-300 hover:border-slate-500 hover:bg-slate-800'
                  }`}
                >
                  {d.code} · {d.title}
                </button>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 border-t border-slate-800/80 pt-4">
              <div className="rounded-xl border border-slate-800 bg-[#0b1424] p-3">
                <p className="text-[10px] uppercase text-slate-400">дисциплины</p>
                <p className="text-xl font-bold text-white mt-0.5">{disciplines.length}</p>
                <p className="text-[11px] text-slate-400">активный семестр</p>
              </div>
              <div className="rounded-xl border border-slate-800 bg-[#0b1424] p-3">
                <p className="text-[10px] uppercase text-slate-400">группы</p>
                <p className="text-xl font-bold text-white mt-0.5">{groups.length || 4}</p>
                <p className="text-[11px] text-slate-400">по всем дисциплинам</p>
              </div>
              <div className="rounded-xl border border-slate-800 bg-[#0b1424] p-3">
                <p className="text-[10px] uppercase text-slate-400">на проверке</p>
                <p className="text-xl font-bold text-white mt-0.5">{submissions.filter((s) => s.status === 'на проверке').length}</p>
                <p className="text-[11px] text-slate-400">текущая дисциплина</p>
              </div>
              <div className="rounded-xl border border-slate-800 bg-[#0b1424] p-3">
                <p className="text-[10px] uppercase text-slate-400">требует внимания</p>
                <p className="text-xl font-bold text-amber-400 mt-0.5">1</p>
                <p className="text-[11px] text-slate-400">текущая группа</p>
              </div>
            </div>
          </div>

          {activeNav === 'disciplines-groups' && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-cyan-500/30 bg-[#09101d] p-6 shadow-xl">
                <div className="mb-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                    Учебный процесс преподавателя
                  </p>
                  <h3 className="text-lg font-bold text-white">Дисциплина → группа → обучающиеся</h3>
                  <p className="text-xs text-slate-300 mt-1">
                    Сначала выберите дисциплину, затем конкретную академическую группу. После этого отображается только состав выбранной группы и функции этой дисциплины.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="rounded-xl border border-cyan-500/20 bg-[#0b1424] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">ШАГ 1 · ДИСЦИПЛИНА</p>
                    <h4 className="text-sm font-bold text-white mt-1 mb-3">Мои дисциплины</h4>
                    <div className="space-y-2.5">
                      {disciplines.map((d) => (
                        <button
                          key={d.code}
                          onClick={() => onSelectDiscipline(d.code)}
                          className={`w-full rounded-xl p-3 text-left transition-all ${
                            selectedDisciplineCode === d.code
                              ? 'border border-cyan-400 bg-cyan-950/70 shadow-md'
                              : 'border border-slate-800 bg-slate-900/60 hover:bg-slate-800/80'
                          }`}
                        >
                          <p className="text-xs font-bold text-white">{d.title}</p>
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            {d.code} · {d.credits} кр. · {d.degree_level}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-cyan-500/20 bg-[#0b1424] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">ШАГ 2 · ГРУППА</p>
                    <h4 className="text-sm font-bold text-white mt-1 mb-3">{selectedDisciplineCode} · группы</h4>
                    <div className="space-y-2.5">
                      {groups.map((g) => (
                        <button
                          key={g.group_code}
                          onClick={() => onSelectGroup(g.group_code)}
                          className={`w-full rounded-xl p-3 text-left transition-all ${
                            selectedGroupCode === g.group_code
                              ? 'border border-cyan-400 bg-cyan-950/70 shadow-md'
                              : 'border border-slate-800 bg-slate-900/60 hover:bg-slate-800/80'
                          }`}
                        >
                          <p className="text-xs font-bold text-white">{g.group_code}</p>
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            {g.student_count} обучающихся · ср. {g.average_score}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-cyan-500/20 bg-[#0b1424] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">ШАГ 3 · ОБУЧАЮЩИЕСЯ</p>
                    <h4 className="text-sm font-bold text-white mt-1 mb-3">{selectedGroupCode || 'Группа'}</h4>
                    <div className="space-y-2.5">
                      {students.map((st) => (
                        <div
                          key={st.full_name}
                          className="rounded-xl border border-slate-800 bg-slate-900/70 p-3"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-xs font-bold text-white">{st.full_name}</p>
                              <p className="text-[10px] text-slate-400 mt-0.5">
                                {st.student_code} · {st.program_name} · {st.course_year} курс
                              </p>
                            </div>
                            <button
                              onClick={() => setSelectedStudentForModal(st)}
                              className="shrink-0 rounded-lg border border-slate-700 bg-slate-800 px-2.5 py-1 text-[11px] font-semibold text-slate-200 hover:border-cyan-400 hover:text-white transition-all"
                            >
                              Карточка
                            </button>
                          </div>
                          <div className="mt-2.5 flex items-center gap-3 border-t border-slate-800/60 pt-2 text-[10px]">
                            <div>
                              <span className="text-slate-400">Посещ.</span>{' '}
                              <span className="font-bold text-emerald-400">{st.attendance_rate}%</span>
                            </div>
                            <div>
                              <span className="text-slate-400">Балл</span>{' '}
                              <span className="font-bold text-cyan-300">{st.current_score}</span>
                            </div>
                            <div>
                              <span className="text-slate-400">Проср.</span>{' '}
                              <span className={`font-bold ${st.overdue_count > 0 ? 'text-rose-400' : 'text-slate-300'}`}>
                                {st.overdue_count}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-cyan-500/30 bg-[#09101d] p-5 shadow-lg">
                <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">Внутри выбранной дисциплины</p>
                <h4 className="text-base font-bold text-white mt-1">{currentDiscipline?.title}</h4>
                <p className="text-xs text-slate-400 mt-1">
                  2026–2027 · осенний семестр · {selectedGroupCode}. Эти функции относятся только к учебному процессу и поэтому не дублируются отдельными карточками преподавателя.
                </p>
              </div>
            </div>
          )}

          {activeNav === 'lessons' && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-cyan-500/30 bg-[#09101d] p-6 shadow-xl">
                <div className="mb-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                    Лента семестра · {currentDiscipline?.title}
                  </p>
                  <h3 className="text-lg font-bold text-white">Недели, занятия, СРО и СРОП</h3>
                  <p className="text-xs text-slate-300 mt-1">
                    Количество недель задаётся дисциплиной. Материалы, задания и консультации связаны с конкретной неделей и видны обучающимся в зеркальном кабинете.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-800/80 pb-4">
                  {Array.from({ length: calendarWeeks }, (_, i) => i + 1).map((w) => (
                    <button
                      key={w}
                      onClick={() => setSelectedWeekNum(w)}
                      className={`rounded-xl px-3 py-2 text-xs font-bold transition-all ${
                        selectedWeekNum === w
                          ? 'border-2 border-cyan-400 bg-cyan-950 text-cyan-300 shadow-md'
                          : 'border border-slate-800 bg-[#0b1424] text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      W{String(w).padStart(2, '0')}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-cyan-500/20 bg-[#0b1424] p-5 flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                        ЛЕКЦИЯ · НЕДЕЛЯ {selectedWeekNum}
                      </span>
                      <h4 className="text-base font-bold text-white mt-1.5">
                        {selectedWeek.lecture_title || `Лекция ${selectedWeekNum}`}
                      </h4>
                      <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                        {selectedWeek.lecture_description || 'Теоретический блок дисциплины.'}
                      </p>
                      <div className="mt-4 rounded-xl border border-slate-800/80 bg-slate-900/60 p-3 text-xs text-slate-400">
                        {selectedWeek.lecture_file_url ? (
                          <button onClick={() => void perform(() => studentApi.downloadCourseFile(selectedWeek.lecture_file_url!))}>Скачать лекцию</button>
                        ) : (
                          <span>Материалы ещё не загружены.</span>
                        )}
                      </div>
                    </div>
                    <div className="mt-5 pt-3 border-t border-slate-800">
                      <label className="text-xs text-cyan-300">Материал лекции
                        <input type="file" aria-label="Материал лекции" onChange={e => { const file = e.target.files?.[0]; if (file) void perform(() => uploadMaterial(file, 'lecture_file_url')); }} />
                      </label>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-emerald-500/20 bg-[#0b1424] p-5 flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                        ПРАКТИКА / СЕМИНАР
                      </span>
                      <h4 className="text-base font-bold text-white mt-1.5">
                        {selectedWeek.practice_title || `Практическое занятие ${selectedWeekNum}`}
                      </h4>
                      <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                        {selectedWeek.practice_description || 'Анализ примеров, практические упражнения и обсуждение результатов.'}
                      </p>
                      <div className="mt-4 rounded-xl border border-slate-800/80 bg-slate-900/60 p-3 text-xs text-slate-400">
                        {selectedWeek.practice_file_url ? (
                          <button onClick={() => void perform(() => studentApi.downloadCourseFile(selectedWeek.practice_file_url!))}>Скачать практику</button>
                        ) : (
                          <span>Файл практического занятия ещё не прикреплён.</span>
                        )}
                      </div>
                    </div>
                    <div className="mt-5 pt-3 border-t border-slate-800">
                      <label className="text-xs text-cyan-300">Файл практики
                        <input type="file" aria-label="Файл практики" onChange={e => { const file = e.target.files?.[0]; if (file) void perform(() => uploadMaterial(file, 'practice_file_url')); }} />
                      </label>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-amber-500/20 bg-[#0b1424] p-5 flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400">СРО</span>
                      <h4 className="text-base font-bold text-white mt-1.5">
                        {selectedWeek.sro_title || `СРО ${selectedWeekNum}`}
                      </h4>
                      <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                        {selectedWeek.sro_description || 'Самостоятельное задание обучающегося с результатом в виде файла, текста или презентации.'}
                      </p>
                      <div className="mt-3 text-xs">
                        <span className="text-slate-400">Дедлайн:</span>{' '}
                        <span className="font-semibold text-amber-300">{selectedWeek.sro_deadline || ''}</span>
                      </div>
                    </div>
                    <div className="mt-5 pt-3 border-t border-slate-800">
                      <button
                        onClick={() => {
                          setNewAssignTitle(selectedWeek.sro_title || `СРО ${selectedWeekNum}`);
                          setNewAssignType('СРО');
                          setNewAssignWeek(selectedWeekNum);
                          setNewAssignDeadline(selectedWeek.sro_deadline || '');
                          setIsAddAssignmentOpen(true);
                        }}
                        className="rounded-xl border border-amber-500/30 bg-amber-950/40 px-4 py-2 text-xs font-semibold text-amber-300 hover:border-amber-400 hover:bg-amber-900/50 transition-all"
                      >
                        Создать задание из СРО
                      </button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-purple-500/20 bg-[#0b1424] p-5 flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400">СРОП</span>
                      <h4 className="text-base font-bold text-white mt-1.5">
                        {selectedWeek.srop_title || `СРОП ${selectedWeekNum}`}
                      </h4>
                      <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                        {selectedWeek.srop_description || 'Самостоятельная работа под руководством преподавателя: вопросы, консультация и фиксация результата.'}
                      </p>
                      <div className="mt-3 text-xs">
                        <span className="text-slate-400">Окно:</span>{' '}
                        <span className="font-semibold text-purple-300">{selectedWeek.srop_window || 'Не задано'}</span>
                      </div>
                    </div>
                    <div className="mt-5 pt-3 border-t border-slate-800">
                      <p className="text-xs text-slate-400">Заполните окно СРОП в редакторе занятия и создайте приглашение в разделе встреч.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeNav === 'assignments' && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-cyan-500/30 bg-[#09101d] p-6 shadow-xl">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                      Задания и проекты
                    </p>
                    <h3 className="text-lg font-bold text-white">{currentDiscipline?.title}</h3>
                    <p className="text-xs text-slate-400 mt-1">
                      Индивидуальные и командные задания, практики, СРО, эссе, презентации и проекты. Каждое задание имеет срок, максимальный балл, критерии и историю сдачи.
                    </p>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <button
                      onClick={() => { setEditingAssignmentId(undefined); setAssignmentStatus('Опубликовано'); setNewAssignTitle(''); setNewAssignDesc(''); setIsAddAssignmentOpen(true); }}
                      className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-2 text-xs font-bold text-slate-950 hover:from-amber-300 hover:to-amber-400 shadow-md transition-all"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Создать задание</span>
                    </button>
                    <button
                      onClick={() => setActiveNav('team-projects')}
                      className="rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-xs font-semibold text-slate-200 hover:border-cyan-400 hover:text-white transition-all"
                    >
                      Командные проекты
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {assignments.map((as) => (
                    <div
                      key={as.id}
                      className="rounded-2xl border border-slate-800 bg-[#0b1424] p-5 shadow-lg space-y-4"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                              {as.assignment_type} · НЕДЕЛЯ {as.week_number}
                            </span>
                            <span className="rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-400 border border-emerald-500/20">
                              {as.status}
                            </span>
                          </div>
                          <h4 className="text-base font-bold text-white mt-1">{as.title}</h4>
                          <p className="text-xs text-slate-400 mt-1 leading-relaxed">{as.description}</p>
                          <p className="text-xs text-slate-400 mt-2">
                            Дедлайн: <span className="font-semibold text-slate-200">{as.deadline}</span> · максимум{' '}
                            <span className="font-semibold text-amber-300">{as.max_points} баллов</span>.
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => setActiveNav('submissions')}
                            className="rounded-xl border border-cyan-500/30 bg-cyan-950/40 px-3.5 py-1.5 text-xs font-semibold text-cyan-300 hover:border-cyan-400 hover:bg-cyan-900/50 transition-all"
                          >
                            Работы обучающихся
                          </button>
                          <button
                            onClick={() => {
                              setEditingAssignmentId(as.id); setAssignmentStatus(as.status);
                              setNewAssignTitle(as.title);
                              setNewAssignType(as.assignment_type);
                              setNewAssignWeek(as.week_number);
                              setNewAssignMaxPoints(as.max_points);
                              setNewAssignDeadline(as.deadline || '');
                              setNewAssignDesc(as.description || '');
                              setIsAddAssignmentOpen(true);
                            }}
                            className="rounded-xl border border-slate-700 bg-slate-800 px-3.5 py-1.5 text-xs font-semibold text-slate-300 hover:text-white transition-all"
                          >
                            Изменить
                          </button>
                        </div>
                      </div>

                      {as.rubric_json && Object.keys(as.rubric_json).length > 0 && (
                        <div className="rounded-xl border border-slate-800/80 bg-slate-900/50 p-3.5">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                            Рубрика оценивания
                          </p>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                            {Object.entries(as.rubric_json).map(([criterion, pts]) => (
                              <div key={criterion} className="rounded-lg bg-slate-800/60 p-2 text-xs">
                                <p className="text-slate-400 truncate">{criterion}</p>
                                <p className="font-bold text-amber-300 mt-0.5">{pts} б.</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeNav === 'submissions' && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-cyan-500/30 bg-[#09101d] p-6 shadow-xl">
                <div className="mb-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                    Очередь проверки работ
                  </p>
                  <h3 className="text-lg font-bold text-white">Работы обучающихся на проверке</h3>
                  <p className="text-xs text-slate-300 mt-1">
                    Проверка отправленных и повторных версий практических работ и СРО. Преподаватель выставляет балл и комментарии.
                  </p>
                </div>

                <div className="space-y-3">
                  {submissions.map((sub) => (
                    <div
                      key={sub.id}
                      className="flex flex-col gap-4 rounded-xl border border-slate-800 bg-[#0b1424] p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white">{sub.student_name}</span>
                          <span className="text-[11px] text-slate-400">({sub.group_code})</span>
                          <span className="rounded-md bg-slate-800 px-2 py-0.5 text-[10px] font-medium text-slate-300 border border-slate-700">
                            версия {sub.version}
                          </span>
                          <span
                            className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${
                              sub.status === 'оценено'
                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                                : sub.status === 'на проверке'
                                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30'
                                : 'bg-blue-500/10 text-blue-400 border border-blue-500/30'
                            }`}
                          >
                            {sub.status}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          Дата сдачи: {sub.submission_date || 'Не указана'}
                          {sub.file_url && <button onClick={() => void perform(() => studentApi.downloadCourseFile(sub.file_url!))} className="ml-3 text-cyan-300">Скачать версию {sub.version}</button>}
                          {sub.feedback_note && <span className="block">Комментарий обучающегося: {sub.feedback_note}</span>}
                        </p>
                        {sub.feedback && (
                          <p className="mt-2 rounded-lg bg-slate-900/60 p-2 text-xs text-slate-300 italic border border-slate-800/80">
                            «{sub.feedback}»
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        {sub.score !== null && sub.score !== undefined ? (
                          <div className="text-right">
                            <span className="text-[10px] text-slate-400 block">Балл</span>
                            <span className="text-base font-bold text-cyan-300">{sub.score} б.</span>
                          </div>
                        ) : null}
                        <button
                          onClick={() => {
                            setGradingSubId(sub.id);
                            setGradingScore(sub.score ?? 0);
                            setGradingFeedback(sub.feedback || '');
                          }}
                          className="rounded-xl border border-cyan-500/30 bg-cyan-950/40 px-3 py-1.5 text-xs font-semibold text-cyan-300 hover:border-cyan-400 hover:bg-cyan-900/50 transition-all"
                        >
                          Оценить
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeNav === 'team-projects' && <CourseTeams code={selectedDisciplineCode} group={selectedGroupCode} students={students} teacher />}

          {activeNav === 'materials' && <section><CourseDocuments code={selectedDisciplineCode} /><h3>Материалы дисциплины</h3>
            {weeks.flatMap(w => [w.lecture_file_url, w.practice_file_url].filter((url): url is string => Boolean(url)).map(url => <button key={url} className="block" onClick={() => void perform(() => studentApi.downloadCourseFile(url))}>Неделя {w.week_number} · Скачать материал · {w.publication_status}</button>))}
            <button onClick={() => setActiveNav('lessons')}>Добавить материалы занятия</button>
          </section>}

          {activeNav === 'analytics' && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-cyan-500/30 bg-[#09101d] p-6 shadow-xl">
                <div className="mb-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                    Аналитика дисциплины
                  </p>
                  <h3 className="text-lg font-bold text-white">Успеваемость и риски потока</h3>
                  <p className="text-xs text-slate-300 mt-1">
                    Сводные показатели успеваемости, среднего балла и регулярности посещения занятий.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-xl border border-slate-800 bg-[#0b1424] p-4">
                    <p className="text-[10px] uppercase text-slate-400">Средний балл группы</p>
                    <p className="text-2xl font-bold text-cyan-300 mt-1">{students.length ? Math.round(students.reduce((n,s) => n+s.current_score,0)/students.length) : 0}</p>
                    <p className="text-[11px] text-emerald-400 mt-1">По текущим данным журнала</p>
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-[#0b1424] p-4">
                    <p className="text-[10px] uppercase text-slate-400">Средняя посещаемость</p>
                    <p className="text-2xl font-bold text-emerald-400 mt-1">{students.length ? Math.round(students.reduce((n,s) => n+s.attendance_rate,0)/students.length) : 0}%</p>
                    <p className="text-[11px] text-slate-400 mt-1">По отмеченным занятиям</p>
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-[#0b1424] p-4">
                    <p className="text-[10px] uppercase text-slate-400">Обучающиеся группы риска</p>
                    <p className="text-2xl font-bold text-amber-400 mt-1">{students.filter(s => s.current_score < 50 || s.attendance_rate < 70).length}</p>
                    <p className="text-[11px] text-slate-400 mt-1">Требуется индивидуальный СРОП</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isAddDisciplineOpen && (
        <div className="cab-overlay">
          <div className="cab-shell w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="text-base font-bold text-white">Добавить дисциплину</h3>
              <button onClick={() => setIsAddDisciplineOpen(false)} className="text-slate-400 hover:text-white">
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-400 mb-1">Код дисциплины</label>
                <input
                  type="text"
                  placeholder="KAZ-421"
                  value={newDiscCode}
                  onChange={(e) => setNewDiscCode(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Название дисциплины</label>
                <input
                  type="text"
                  placeholder="Современный казахский язык"
                  value={newDiscTitle}
                  onChange={(e) => setNewDiscTitle(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Кредиты</label>
                  <input
                    type="number"
                    value={newDiscCredits}
                    onChange={(e) => setNewDiscCredits(Number(e.target.value))}
                    className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Уровень</label>
                  <select
                    value={newDiscLevel}
                    onChange={(e) => setNewDiscLevel(e.target.value)}
                    className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none"
                  >
                    <option value="Бакалавриат">Бакалавриат</option>
                    <option value="Магистратура">Магистратура</option>
                    <option value="Докторантура">Докторантура</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-800">
              <button
                onClick={() => setIsAddDisciplineOpen(false)}
                className="rounded-xl border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
              >
                Отмена
              </button>
              <button
                onClick={handleCreateDisciplineSubmit}
                className="rounded-xl bg-cyan-500 px-4 py-2 text-xs font-bold text-slate-950 hover:bg-cyan-400"
              >
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddAssignmentOpen && (
        <div className="cab-overlay">
          <div className="cab-shell w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="text-base font-bold text-white">Создать задание</h3>
              <button onClick={() => setIsAddAssignmentOpen(false)} className="text-slate-400 hover:text-white">
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 text-xs">
              <label>Публикация <select value={assignmentStatus} onChange={e => setAssignmentStatus(e.target.value)}><option>Черновик</option><option>Опубликовано</option><option>Закрыто</option></select></label>
              <div>
                <label className="block text-slate-400 mb-1">Название задания</label>
                <input
                  type="text"
                  placeholder="Практическая работа..."
                  value={newAssignTitle}
                  onChange={(e) => setNewAssignTitle(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Тип задания</label>
                  <select
                    value={newAssignType}
                    onChange={(e) => setNewAssignType(e.target.value)}
                    className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none"
                  >
                    <option value="ПРАКТИКА">ПРАКТИКА</option>
                    <option value="СРО">СРО</option>
                    <option value="СЕМИНАР">СЕМИНАР</option>
                    <option value="ПРОЕКТ">ПРОЕКТ</option>
                    <option>РК1</option><option>РК2</option><option>ИТОГ</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Неделя (1–15)</label>
                  <input
                    type="number"
                    min={1}
                    max={15}
                    value={newAssignWeek}
                    onChange={(e) => setNewAssignWeek(Number(e.target.value))}
                    className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Макс. балл</label>
                  <input
                    type="number"
                    value={newAssignMaxPoints}
                    onChange={(e) => setNewAssignMaxPoints(Number(e.target.value))}
                    className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Дедлайн</label>
                  <input
                    type="text"
                    placeholder="2026-10-15"
                    value={newAssignDeadline}
                    onChange={(e) => setNewAssignDeadline(e.target.value)}
                    className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Описание и методические указания</label>
                <textarea
                  rows={3}
                  value={newAssignDesc}
                  onChange={(e) => setNewAssignDesc(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-800">
              <button
                onClick={() => setIsAddAssignmentOpen(false)}
                className="rounded-xl border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
              >
                Отмена
              </button>
              <button
                onClick={() => void perform(handleCreateAssignmentSubmit)}
                className="rounded-xl bg-amber-400 px-4 py-2 text-xs font-bold text-slate-950 hover:bg-amber-300"
              >
                Сохранить задание
              </button>
            </div>
          </div>
        </div>
      )}

      {gradingSubId && (
        <div className="cab-overlay">
          <div className="cab-shell w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="text-base font-bold text-white">Оценить работу</h3>
              <button onClick={() => setGradingSubId(null)} className="text-slate-400 hover:text-white">
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-400 mb-1">Балл (максимум задания: {assignments.find(a => a.id === submissions.find(s => s.id === gradingSubId)?.assignment_id)?.max_points || 10})</label>
                <input
                  type="number"
                  step="0.5"
                  value={gradingScore}
                  onChange={(e) => setGradingScore(Number(e.target.value))}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-base font-bold text-cyan-300"
                />
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Рецензия и замечания</label>
                <textarea
                  rows={3}
                  value={gradingFeedback}
                  onChange={(e) => setGradingFeedback(e.target.value)}
                  placeholder="Отличная аргументация, замечаний нет."
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-800">
              <button
                onClick={() => setGradingSubId(null)}
                className="rounded-xl border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
              >
                Отмена
              </button>
              <button disabled={!gradingFeedback.trim()} onClick={() => void perform(async () => {
                await onGradeSubmission(gradingSubId, null, gradingFeedback, 'доработка'); setGradingSubId(null);
              })} className="text-amber-300 disabled:opacity-50">Вернуть на доработку</button>
              <button
                onClick={() => void perform(handleGradeSubmit)}
                className="rounded-xl bg-cyan-400 px-4 py-2 text-xs font-bold text-slate-950 hover:bg-cyan-300"
              >
                Сохранить оценку
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedStudentForModal && (
        <div className="cab-overlay">
          <div className="cab-shell w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <AcademicCapIcon className="h-5 w-5 text-cyan-400" />
                <h3 className="text-base font-bold text-white">Карточка обучающегося</h3>
              </div>
              <button onClick={() => setSelectedStudentForModal(null)} className="text-slate-400 hover:text-white">
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 text-xs">
              <div>
                <p className="text-[10px] uppercase text-slate-400">ФИО</p>
                <p className="text-base font-bold text-white">{selectedStudentForModal.full_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase text-slate-400">Шифр программы</p>
                  <p className="font-semibold text-slate-200">{selectedStudentForModal.student_code}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-slate-400">Курс</p>
                  <p className="font-semibold text-slate-200">{selectedStudentForModal.course_year} курс</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase text-slate-400">Образовательная программа</p>
                <p className="font-semibold text-slate-200">{selectedStudentForModal.program_name}</p>
              </div>
              <div className="grid grid-cols-3 gap-2 rounded-xl border border-slate-800 bg-slate-800/50 p-3 text-center">
                <div>
                  <p className="text-[10px] text-slate-400">Посещаемость</p>
                  <p className="text-sm font-bold text-emerald-400 mt-0.5">{selectedStudentForModal.attendance_rate}%</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400">Текущий балл</p>
                  <p className="text-sm font-bold text-cyan-300 mt-0.5">{selectedStudentForModal.current_score}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400">Просрочки</p>
                  <p className="text-sm font-bold text-slate-200 mt-0.5">{selectedStudentForModal.overdue_count}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end pt-3 border-t border-slate-800">
              <button
                onClick={() => setSelectedStudentForModal(null)}
                className="rounded-xl bg-slate-800 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-700"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
